import {useState, useEffect} from 'react'
import moment from 'moment-timezone'
import {
  useLazyGetWalletCardReportQuery,
  useLazyGetWalletCardReportStackedQuery,
  useLazyGetWalletCardReportChartDetailQuery,
} from '../../../../../services/cellcast.v2'
import {StackedBarSeries, CardMetricSeries, StackedTotal} from '../types'
import {cleanSeriesData, cleanCardMetricSeries, formatCategories} from '../utils'

export const useWalletCardReport = () => {
  const [whereWhenDate, setWhereWhenDate] = useState<{start: Date; end: Date}>({
    start: new Date(moment().subtract(7, 'days').format('YYYY-MM-DD')),
    end: new Date(),
  })

  const [selectedCard, setSelectedCard] = useState<string>('')
  const [cardList, setCardList] = useState<string[]>([])
  const [comparisonSeries, setComparisonSeries] = useState<CardMetricSeries[]>([])
  const [comparisonCategories, setComparisonCategories] = useState<string[]>([])
  const [stackedSeries, setStackedSeries] = useState<StackedBarSeries[]>([])
  const [stackedCategories, setStackedCategories] = useState<string[]>([])
  const [stackedTotal, setStackedTotal] = useState<StackedTotal>({
    totalBites: 0,
    totalBitesGrowth: 0,
    totalLeads: 0,
    totalLeadGrowth: 0,
    totalSales: 0,
    totalSalesGrowth: 0,
    totalWarmUp: 0,
    totalWarmUpGrowth: 0,
  })
  const [getReport, reportState] = useLazyGetWalletCardReportQuery()
  const [getStackedReport] = useLazyGetWalletCardReportStackedQuery()
  const [getChartDetail] = useLazyGetWalletCardReportChartDetailQuery()

  const whereWhen = {
    start: moment(whereWhenDate.start).format('YYYY-MM-DD'),
    end: moment(whereWhenDate.end).format('YYYY-MM-DD'),
  }

  const ALL_CARD_OPTION = 'All'

  // Fetch all reports when date changes
  useEffect(() => {
    if (whereWhenDate.start && whereWhenDate.end) {
      // Fetch main report
      getReport({
        startDate: whereWhen.start,
        endDate: whereWhen.end,
      })

      // Fetch stacked report
      getStackedReport({fromDate: whereWhen.start, toDate: whereWhen.end})
        .unwrap()
        .then((res) => {
          const sData = res?.data?.stackedBarData || []
          const dates = res?.data?.dates || []

          const cleanedSeries = cleanSeriesData(sData)
          const formattedCategories = formatCategories(dates)

          setStackedSeries(cleanedSeries)
          setStackedCategories(formattedCategories)
          setStackedTotal({
            totalBites: res?.data?.totalBites || 0,
            totalBitesGrowth: res?.data?.totalBitesGrowth || 0,
            totalLeads: res?.data?.totalLeads || 0,
            totalLeadGrowth: res?.data?.totalLeadGrowth || 0,
            totalSales: res?.data?.totalSales || 0,
            totalSalesGrowth: res?.data?.totalSalesGrowth || 0,
            totalWarmUp: res?.data?.totalWarmUp || 0,
            totalWarmUpGrowth: res?.data?.totalWarmUpGrowth || 0,
          })
        })
        .catch(() => {
          setStackedSeries([])
          setStackedCategories([])
        })

      // Fetch chart detail
      getChartDetail({fromDate: whereWhen.start, toDate: whereWhen.end})
        .unwrap()
        .then((res) => {
          const detail = res.data
          const cardsMetrics = detail.cardMetrics || {}
          const datesArr = detail.dates || []

          setComparisonCategories(formatCategories(datesArr))
          const cards = Object.keys(cardsMetrics)
          // Include the special "All" option at the beginning of the list
          const cardsWithAll = [ALL_CARD_OPTION, ...cards]
          setCardList(cardsWithAll)

          // Default to "All" on initial load
          setSelectedCard((prev) => (prev ? prev : ALL_CARD_OPTION))

          // Prepare comparison series for default selection
          let initialSeriesData: any[] = []
          if (cardsWithAll.length > 0) {
            if (ALL_CARD_OPTION === cardsWithAll[0]) {
              // Aggregate all card metrics
              const aggregatedMap: Record<string, number[]> = {}
              Object.values(cardsMetrics).forEach((cardSeries: any) => {
                ;(cardSeries as any[]).forEach((series: any) => {
                  const dataArr: number[] = Array.isArray(series?.data)
                    ? series.data.map((d: any) => Number(d) || 0)
                    : []
                  if (aggregatedMap[series.name]) {
                    aggregatedMap[series.name] = aggregatedMap[series.name].map(
                      (v, idx) => v + (dataArr[idx] || 0)
                    )
                  } else {
                    aggregatedMap[series.name] = [...dataArr]
                  }
                })
              })
              initialSeriesData = Object.keys(aggregatedMap).map((name) => ({
                name,
                data: aggregatedMap[name],
              }))
            } else {
              initialSeriesData = cardsMetrics[cardsWithAll[0]] || []
            }
          }

          const cleanedInitialSeries = cleanCardMetricSeries(initialSeriesData)
          setComparisonSeries(cleanedInitialSeries)
        })
        .catch(() => {
          setComparisonSeries([])
          setCardList([])
          setComparisonCategories([])
        })
    }
  }, [whereWhenDate, getReport, getStackedReport, getChartDetail])

  // Update comparison series when selected card changes
  useEffect(() => {
    if (!selectedCard) return

    getChartDetail({fromDate: whereWhen.start, toDate: whereWhen.end})
      .unwrap()
      .then((res) => {
        const cardsMetrics = res.data?.cardMetrics || {}

        let rawSeries: any[] = []

        if (selectedCard === ALL_CARD_OPTION) {
          // Aggregate metrics across all cards
          const aggregatedMap: Record<string, number[]> = {}
          Object.values(cardsMetrics).forEach((cardSeries: any) => {
            ;(cardSeries as any[]).forEach((series: any) => {
              const dataArr: number[] = Array.isArray(series?.data)
                ? series.data.map((d: any) => Number(d) || 0)
                : []
              if (aggregatedMap[series.name]) {
                aggregatedMap[series.name] = aggregatedMap[series.name].map(
                  (v, idx) => v + (dataArr[idx] || 0)
                )
              } else {
                aggregatedMap[series.name] = [...dataArr]
              }
            })
          })
          rawSeries = Object.keys(aggregatedMap).map((name) => ({
            name,
            data: aggregatedMap[name],
          }))
        } else {
          rawSeries = cardsMetrics[selectedCard] || []
        }

        const seriesData = cleanCardMetricSeries(rawSeries)
        setComparisonSeries(seriesData)
      })
      .catch(() => {})
  }, [selectedCard, getChartDetail, whereWhen.start, whereWhen.end])

  return {
    // State
    whereWhenDate,
    selectedCard,
    cardList,
    comparisonSeries,
    comparisonCategories,
    stackedSeries,
    stackedCategories,
    stackedTotal,
    // Actions
    setWhereWhenDate,
    setSelectedCard,

    // API state
    reportState,

    // Computed values
    whereWhen,
  }
}
